/**
 * TRIED ON, AND THE FAMILY'S OWN PIECES IN HAND (Bobbie, 2026-11-02: "lets add
 * a tried on costume option for the paretns and kids as well. where they can
 * mark off that it has been tried on" and "if parents need to buy a costum
 * epiece they should have a place they can mark off that it has arrived so
 * that teachers and diretors can see who has it in hand and who does not
 * yet", with "it should track who does what ... and exactly when").
 *
 * WHERE IT LIVES. Server only, beside Kim's own per-dancer answers:
 * costumeDancerRecords/{studentId}.costumes.{costumeId}
 *   triedOn     { at, byUid, byName, via }, absent until someone marks it
 *   have        { [pieceKey]: { at, byUid, byName, via } }, one per piece
 *   inHandLog   every mark and unmark, oldest first, the last
 *               COSTUME_IN_HAND_LOG_MAX kept: { what, piece, value, at,
 *               byUid, byName, via }
 * No client rules (the collection has none), so the only doors are
 * netlify/functions/costumeInHand.mjs (a parent, her own email Jr Staff
 * sign-in, the desk's report, a teacher's class panel) and the dancer
 * portal's setCostumeInHand action (her own dancer sign-in). A screen gets
 * names and times only, never a uid.
 *
 * THE FAMILY'S PIECES. Whatever the card asks the family to bring: the costume
 * itself on a "Parent needs to find primary costume" card (family-supplied),
 * the shoes, the tights, and each "Parent/family needs" item. Each has a
 * stable key, so a mark stays on its piece while the list around it changes.
 *
 * PURE. No React, no Firebase, only .ts-suffixed local imports, so the server
 * lanes, the screens and the tests read the same answers.
 */
import {
  isCostumeApprovedForPickup,
  pickupStateFor,
  wearsCostume,
  type CostumePickupCard,
} from './costumePickupState.ts';
import { studioStamp } from './costumeTrail.ts';
import { isOwnJrStaffSignIn } from './jrStaffAliases.ts';

export type InHandVia = 'family' | 'dancer';

export interface InHandStamp {
  atMs: number | null;
  byName: string | null;
  via: InHandVia | null;
}

export interface CostumeInHand {
  triedOn: InHandStamp | null;
  have: Record<string, InHandStamp>;
}

/** The card fields this module reads; a raw Firestore record and a CostumeDoc both fit. */
export interface InHandCard extends CostumePickupCard {
  name?: string | null;
  extrasRequired?: unknown;
  shoeColor?: string | null;
  shoeType?: string | null;
  tights?: string | null;
}

export type FamilyPieceKind = 'costume' | 'shoes' | 'tights' | 'extra';

export interface FamilyPiece {
  key: string;
  label: string;
  kind: FamilyPieceKind;
}

/** Marks kept on one dancer's one costume. A season is a handful. */
export const COSTUME_IN_HAND_LOG_MAX = 40;
/** The most pieces one card may list (extrasRequired is capped at 50 on the wire). */
export const COSTUME_IN_HAND_MAX_PIECES = 60;

function text(value: unknown): string {
  return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
}

/** FNV-1a, 32 bit, as 8 hex digits. Stable across server and phone. */
function fnv1a(value: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

/** One "Parent/family needs" item's key: the same words, the same key, whatever case or spacing. */
export function extraPieceKey(item: string): string {
  return `extra-${fnv1a(text(item).toLowerCase())}`;
}

/**
 * Every piece this card asks the family to bring, in the order the card lists
 * them: the costume itself (family-supplied cards only), shoes, tights, then
 * each extra. An extra listed twice is one piece.
 */
export function familyPiecesFor(card: InHandCard | null | undefined): FamilyPiece[] {
  if (!card) return [];
  const pieces: FamilyPiece[] = [];
  if (card.provisionType === 'family-supplied') {
    pieces.push({ key: 'costume', label: `Costume: ${text(card.name) || 'Costume'}`, kind: 'costume' });
  }
  const shoes = [text(card.shoeColor), text(card.shoeType)].filter(Boolean).join(' ');
  if (shoes) pieces.push({ key: 'shoes', label: `Shoes: ${shoes}`, kind: 'shoes' });
  const tights = text(card.tights);
  if (tights) pieces.push({ key: 'tights', label: `Tights: ${tights}`, kind: 'tights' });
  const seen = new Set(pieces.map((p) => p.key));
  for (const raw of Array.isArray(card.extrasRequired) ? card.extrasRequired : []) {
    const label = text(raw);
    if (!label) continue;
    const key = extraPieceKey(label);
    if (seen.has(key)) continue;
    seen.add(key);
    pieces.push({ key, label, kind: 'extra' });
    if (pieces.length >= COSTUME_IN_HAND_MAX_PIECES) break;
  }
  return pieces;
}

/**
 * Why Tried on cannot be marked yet, or 'ok'.
 *   not-approved  Kim has not approved the card
 *   not-hers      Kim switched this dancer off the card (notWornBy)
 *   not-set       the card says neither studio nor family provides it
 *   not-arrived   a studio-ordered costume that is not at the studio or home
 *                 yet, for this dancer (her own "arrived: No" counts)
 * A family-supplied costume may be marked any time: the family has it or
 * is finding it, and only they know when it went on.
 */
export type TriedOnBlock = 'ok' | 'not-approved' | 'not-hers' | 'not-set' | 'not-arrived';

const ARRIVED = new Set(['arrived', 'picked-up']);

export function triedOnBlock(card: InHandCard, studentId: string, ownArrived: boolean | null = null): TriedOnBlock {
  if (!isCostumeApprovedForPickup(card)) return 'not-approved';
  if (!studentId || !wearsCostume(card, studentId)) return 'not-hers';
  if (card.provisionType === 'family-supplied') return 'ok';
  if (card.provisionType !== 'studio-ordering') return 'not-set';
  if (ownArrived === false || card.arrivedByDancer?.[studentId] === false) return 'not-arrived';
  // Kim's own Yes for HER costume opens it even while the class card still
  // says ordered (Codex review on #1511).
  if (ownArrived === true || card.arrivedByDancer?.[studentId] === true) return 'ok';
  const state = pickupStateFor(card, studentId);
  if (state.kind === 'picked-up' || state.kind === 'ready') return 'ok';
  return ARRIVED.has(String(card.orderStatus ?? '')) ? 'ok' : 'not-arrived';
}

/** Why a piece cannot be marked, or 'ok': the card is hers and lists it. */
export function pieceBlock(card: InHandCard, studentId: string, pieceKey: string): 'ok' | 'not-approved' | 'not-hers' | 'no-such-piece' {
  if (!isCostumeApprovedForPickup(card)) return 'not-approved';
  if (!studentId || !wearsCostume(card, studentId)) return 'not-hers';
  return familyPiecesFor(card).some((p) => p.key === pieceKey) ? 'ok' : 'no-such-piece';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function millisOf(raw: Record<string, unknown>): number | null {
  if (typeof raw.atMs === 'number' && Number.isFinite(raw.atMs) && raw.atMs > 0) return raw.atMs;
  const at = raw.at;
  if (at && typeof at === 'object') {
    const toMillis = (at as { toMillis?: unknown }).toMillis;
    if (typeof toMillis === 'function') {
      try {
        const ms = toMillis.call(at);
        return typeof ms === 'number' && Number.isFinite(ms) && ms > 0 ? ms : null;
      } catch {
        return null;
      }
    }
  }
  return null;
}

/**
 * One stored or sent stamp as a screen may see it: when, the name, which door.
 * Never the uid. Anything malformed reads as no stamp.
 */
export function inHandStampFrom(raw: unknown): InHandStamp | null {
  if (!isRecord(raw)) return null;
  const byName = text(raw.byName).slice(0, 120);
  return {
    atMs: millisOf(raw),
    byName: byName || null,
    via: raw.via === 'family' || raw.via === 'dancer' ? raw.via : null,
  };
}

/**
 * One dancer's one costume entry (costumeDancerRecords ... .costumes.{id}, or
 * the same shape sent over the wire) as a screen may see it.
 */
export function inHandFrom(entry: unknown): CostumeInHand {
  const out: CostumeInHand = { triedOn: null, have: {} };
  if (!isRecord(entry)) return out;
  out.triedOn = inHandStampFrom(entry.triedOn);
  if (isRecord(entry.have)) {
    for (const [key, raw] of Object.entries(entry.have)) {
      if (!/^[A-Za-z0-9-]{1,40}$/.test(key)) continue;
      const stamp = inHandStampFrom(raw);
      if (stamp) out.have[key] = stamp;
    }
  }
  return out;
}

/** The same answer with only the keys a screen may see, ready for JSON. */
export function publicInHand(entry: unknown): CostumeInHand {
  return inHandFrom(entry);
}

/** "Ann, Tue Nov 3, 4:12pm"; a nameless mark is never given a made-up name. */
export function inHandWho(stamp: InHandStamp | null | undefined): string {
  if (!stamp) return '';
  return [stamp.byName?.trim() || '', studioStamp(stamp.atMs)].filter(Boolean).join(', ');
}

/** The pieces she has, of the ones the card lists now (an old mark on a removed item does not count). */
export function piecesInHand(pieces: readonly FamilyPiece[], inHand: CostumeInHand | null | undefined): { have: number; total: number; missing: FamilyPiece[] } {
  const marks = inHand?.have ?? {};
  const missing = pieces.filter((p) => !marks[p.key]);
  return { have: pieces.length - missing.length, total: pieces.length, missing };
}

export interface ClassInHandDancer extends CostumeInHand {
  studentId: string;
  name: string;
  triedOnOpen: boolean;
}

export interface ClassInHandCostume {
  id: string;
  name: string;
  familySupplied: boolean;
  pieces: FamilyPiece[];
  dancers: ClassInHandDancer[];
}

/**
 * The class page's read-only panel, parsed field by field: the costumeInHand
 * Function's GET and the dancer portal's classCostumeInHand action send the
 * same shape (netlify/functions/lib/costumeInHandClass.mjs).
 */
export function classInHandFromWire(payload: unknown): ClassInHandCostume[] {
  if (!isRecord(payload) || !Array.isArray(payload.costumes)) return [];
  return payload.costumes.filter(isRecord).map((c) => ({
    id: String(c.id ?? ''),
    name: typeof c.name === 'string' ? c.name : 'Costume',
    familySupplied: c.familySupplied === true,
    pieces: (Array.isArray(c.pieces) ? c.pieces : []).filter(isRecord).map((p) => ({
      key: String(p.key ?? ''),
      label: String(p.label ?? ''),
      kind: (['costume', 'shoes', 'tights', 'extra'].includes(String(p.kind)) ? p.kind : 'extra') as FamilyPiece['kind'],
    })).filter((p) => p.key),
    dancers: (Array.isArray(c.dancers) ? c.dancers : []).filter(isRecord).map((d) => ({
      studentId: String(d.studentId ?? ''),
      name: typeof d.name === 'string' ? d.name : 'Dancer',
      triedOnOpen: d.triedOnOpen === true,
      ...inHandFrom(d),
    })),
  })).filter((c) => c.id);
}

/** The viewer on a dancer's family profile, as her users doc reads. */
export interface InHandViewer {
  uid: string;
  role?: string | null;
  status?: string | null;
  studentId?: string | null;
  authMode?: string | null;
  jrStaffPromoted?: boolean;
}

/**
 * May this viewer mark Tried on and the family's pieces for this dancer on
 * her family profile? The same two doors the costumeInHand Function opens
 * (inHandMarkerVia): her own active grown-up on her familyUids, or the dancer
 * herself on her own email Jr Staff account.
 */
export function canMarkInHandOnProfile(
  viewer: InHandViewer | null | undefined,
  student: { id: string; familyUids: readonly string[] } | null | undefined,
): boolean {
  if (!viewer || !student || viewer.status !== 'active') return false;
  // Her own sign-in, resolved through the shared alias helper (DECISIONS 26
  // and 27). The Function lets an active 'ensemble' account carrying her
  // studentId mark, so this asks for exactly that shape: her unpromoted
  // dancer sign-in marks through the dancer portal instead.
  if (viewer.studentId) {
    return viewer.studentId === student.id && viewer.role === 'ensemble' && isOwnJrStaffSignIn(viewer);
  }
  return student.familyUids.includes(viewer.uid);
}

/**
 * The family checklist shows on a card the dancer actually has: never on a
 * card still waiting on the studio's approval, never on one Kim switched her
 * off (a staff parent can see both).
 */
export function showsInHandChecklist(card: InHandCard, studentId: string): boolean {
  const block = triedOnBlock(card, studentId);
  return block !== 'not-approved' && block !== 'not-hers';
}

export const TRIED_ON_BLOCK_WORDS: Record<Exclude<TriedOnBlock, 'ok'>, string> = {
  'not-approved': 'Tried on opens once the studio approves this costume.',
  'not-hers': 'This costume is not hers.',
  'not-set': 'Tried on opens once the studio says who provides this costume.',
  'not-arrived': 'Tried on opens once the costume arrives.',
};
